import powerAgentsApi from 'dashboard/api/powerAgents';

// O transporte do Playground para a conversa de teste (L-26): o que `useConversaDoMotor` pede, falando
// com o RASCUNHO do agente pelas rotas do painel. A conversa e a tela são as da página do link
// (`shared/components/conversaDeTeste`); o que muda é com quem se fala e o que volta:
// - a foto e o PDF sobem em dois passos (o upload devolve as referências, e o turno as leva), e
//   aqui isso vira um passo só, como o `enviarComAnexos` da página. Com a nota de voz (A-16), o
//   upload começa enquanto ela é transcrita (`subirAnexos`), e o turno leva o `audio_id` e as
//   referências juntos;
// - as rotas do painel dizem o código do erro em `error`, e a conversa lê `erro`; o `tentar_em` do
//   runtime ocupado vem dentro de `detail`, que o painel repassa como veio. Em texto, o `detail` é
//   o motivo da recusa (a ferramenta malformada do rascunho, por exemplo), e segue como `detalhe`,
//   que a conversa mostra à equipe;
// - o resultado vem do rascunho no formato do rastro (`TraceProjection.rodada`) e vira o turno da
//   página (`turnoDoResultado`).
//
// A sessão dá memória ao teste entre uma mensagem e a seguinte. O "Recomeçar" apaga a do runtime e só
// então passa a usar uma nova: se o servidor não confirmar, a conversa e a memória ficam.
//
// `aoRecusarTranscricao` é chamado quando o upload da nota de voz é recusado porque a transcrição foi
// desligada depois de a tela abrir: quem usa esconde o microfone e busca o agente de novo.

const novaSessao = () => window.crypto.randomUUID();

// A falha do painel no vocabulário da conversa. Sem resposta (a rede), ela segue como veio.
export const noVocabularioDaConversa = e => {
  if (!e?.response) return e;
  const { data, status } = e.response;
  const {
    error: erro,
    detail: detalhe,
    ...resto
  } = data && typeof data === 'object' ? data : {};
  const tentarEm = Number(detalhe?.tentar_em);
  const motivo = typeof detalhe === 'string' ? detalhe.trim() : '';
  return Object.assign(new Error(e.message), {
    response: {
      status,
      data: {
        ...resto,
        erro,
        ...(tentarEm > 0 ? { tentar_em: Math.ceil(tentarEm) } : {}),
        ...(motivo ? { detalhe: motivo } : {}),
      },
    },
  });
};

const traduzindo = promessa =>
  promessa.catch(e => {
    throw noVocabularioDaConversa(e);
  });

// O resultado do rascunho como o turno da página: as partes, as imagens (só https, como a página
// mostra), a transferência, os arquivos não lidos com o motivo, o tipo do erro e o detalhe dele (que
// o servidor manda cru ao super admin e limpo aos outros). A hora da resposta o resultado do
// rascunho não traz: é a do navegador, quando ele chega. O rastro vai à parte, para o slot
// `#depois-da-resposta`, com a passagem a uma pessoa, a manutenção e os arquivos ao lado, como o
// `RastroDoTurno` o lê.
export const turnoDoResultado = resultado => {
  if (resultado.status === 'pending') return { status: 'pending' };
  const respondidaEm = new Date().toISOString();
  const rastro = resultado.turn
    ? {
        ...resultado.turn,
        handoff: resultado.handoff,
        maintenance: resultado.maintenance,
        attachments: resultado.attachments,
      }
    : null;
  if (resultado.status !== 'done')
    return {
      status: 'error',
      erro: resultado.error_kind || null,
      detalhe: resultado.error || null,
      respondida_em: respondidaEm,
      rastro,
    };
  return {
    status: 'done',
    partes: resultado.reply?.parts || [],
    imagens: (resultado.reply?.images || [])
      .filter(imagem => String(imagem?.url).startsWith('https://'))
      .map(({ url, caption }) => ({ url, caption })),
    transferencia: resultado.handoff?.outcome === 'ok',
    nao_lidos: (resultado.attachments || [])
      .filter(arquivo => arquivo.read === false)
      .map(arquivo => ({ nome: arquivo.file_name, motivo: arquivo.reason })),
    respondida_em: respondidaEm,
    rastro,
  };
};

export function transporteDoRascunho(
  ctx,
  { aoRecusarTranscricao = () => {} } = {}
) {
  let sessao = novaSessao();
  const rascunho = (chamada, ...argumentos) =>
    traduzindo(chamada(ctx.accountId(), ctx.agentId(), ...argumentos));
  // As referências dos arquivos, que o turno leva.
  const subirAnexos = arquivos =>
    rascunho(powerAgentsApi.playgroundFilesUpload, arquivos).then(
      ({ anexos }) => anexos.map(anexo => anexo.id)
    );

  return {
    enviar: texto => rascunho(powerAgentsApi.playgroundDraft, sessao, texto),
    enviarComAnexos: (texto, arquivos) =>
      subirAnexos(arquivos).then(referencias =>
        rascunho(
          powerAgentsApi.playgroundDraft,
          sessao,
          texto,
          null,
          referencias
        )
      ),
    subirAnexos,
    // Com arquivos, as referências do upload que começou com a transcrição, ou, no reenvio, de um
    // upload novo.
    enviarNotaDeVoz: async (audioId, arquivos = [], subidos = null) => {
      if (!arquivos.length)
        return rascunho(powerAgentsApi.playgroundDraft, sessao, null, audioId);
      const referencias = await (subidos || subirAnexos(arquivos));
      return rascunho(
        powerAgentsApi.playgroundDraft,
        sessao,
        null,
        audioId,
        referencias
      );
    },
    resultado: pedido =>
      rascunho(powerAgentsApi.playgroundDraftResult, pedido).then(
        turnoDoResultado
      ),
    subirAudio: (arquivo, duracaoMs) =>
      rascunho(powerAgentsApi.playgroundAudioUpload, arquivo, duracaoMs).catch(
        e => {
          if (e?.response?.data?.erro === 'transcricao_desligada')
            aoRecusarTranscricao();
          throw e;
        }
      ),
    audio: audioId => rascunho(powerAgentsApi.playgroundAudioResult, audioId),
    recomecar: async () => {
      await rascunho(powerAgentsApi.playgroundDraftReset, sessao);
      sessao = novaSessao();
    },
    // O Playground é uma seção do painel: nada o tira da conversa, e a falha fica no turno.
    saida: () => null,
  };
}
